/**
 * Transform agentloop-stream blocks into display message blocks
 */

import type { Block as AgentBlock } from '../../../lib/agentloop-stream';
import type { MessageBlock } from '../types';

/**
 * Convert agentloop-stream blocks to display message blocks
 * - Filters to complete blocks (and streaming shell_exec)
 * - Sorts by UUID v7 (chronological)
 * - Groups consecutive assistant text messages
 */
export function transformBlocks(
  blocks: Map<string, AgentBlock>,
  latestError?: { id: string; content: string; errorType?: string }
): MessageBlock[] {
  const result: MessageBlock[] = [];

  // Get all COMPLETE blocks and INCOMPLETE shell_exec blocks, sorted by UUID v7
  const completeBlocks = Array.from(blocks.values())
    .filter(
      (block) =>
        block.isComplete ||
        (block.type === 'tool' &&
          block.toolName === 'shell_exec' &&
          !block.isComplete)
    )
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  let currentAssistantMessage: MessageBlock | null = null;

  for (const block of completeBlocks) {
    // Text blocks
    if (block.type === 'text' && block.content.trim()) {
      const role = block.role || 'assistant';

      if (role === 'assistant' && currentAssistantMessage?.type === 'assistant') {
        // Merge consecutive assistant messages
        currentAssistantMessage.content += block.content;
      } else {
        // Start new message block
        if (role === 'assistant') {
          currentAssistantMessage = {
            id: block.id,
            type: 'assistant',
            content: block.content,
            isComplete: block.isComplete,
          };
          result.push(currentAssistantMessage);
        } else {
          // User message
          currentAssistantMessage = null;
          result.push({
            id: block.id,
            type: 'user',
            content: block.content,
            isComplete: block.isComplete,
          });
        }
      }
    }
    // Reasoning blocks
    else if (block.type === 'reasoning' && block.content.trim()) {
      currentAssistantMessage = null;
      result.push({
        id: block.id,
        type: 'reasoning',
        content: block.content,
        isComplete: block.isComplete,
      });
    }
    // Tool blocks
    else if (block.type === 'tool' && block.toolName) {
      currentAssistantMessage = null;
      result.push({
        id: block.id,
        type: 'tool',
        toolCallId: block.toolCallId || block.id,
        toolName: block.toolName,
        toolInput: block.toolInput,
        toolOutput: block.toolOutput,
        toolIsError: block.toolIsError,
        isStreaming: !block.isComplete,
        isComplete: block.isComplete,
      });
    }
  }

  // Add latest error at the end if present
  if (latestError) {
    result.push({
      id: latestError.id,
      type: 'error',
      content: latestError.content,
      errorType: latestError.errorType as any,
      isComplete: true,
    });
  }

  return result;
}
